import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import InboxUtils from "Utils/InboxUtils";
import { useHistory } from "react-router-dom";
import SecondaryCheckbox from "Components/common/SecondaryCheckbox";
import { MP_EVENT } from "Static/constant";
import Utils from "Utils/utils";
import { sequenceStoreData } from "Stores/sequenceData";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import userDetail from "Stores/userDetail";
import { editSequencePopupState } from "Components/common/EditSequencePopup";
import PaywallUtils from "Utils/PaywallUtils";
import { SequenceUtils } from "Utils/SequenceUtils";
import SearchInputBox from "Components/common/SearchInputBox";
import InboxListItem from "./InboxListItem";
import SelectedInboxList from "./SelectedInboxList";
import makeApi, { URLS } from "Utils/apiURL";
import UsageAuditModal from "./UsageAuditModal";

const MultiInboxSelection = observer(props => {
  const history = useHistory();
  const {
    title = "Sending inboxes",
    sequence = {},
    reAssignContact = false,
    setReAssignContact = () => { },
    sequenceId = "",
    isSharedSequence = false,
    isInboxMappedToSequence = false,
    getSelectedSeqData = () => { },
    sequenceMemberId = "",
    hasSubLabel = true,
    showHeader = true,
    showReassignInbox = true,
    className = "",
    cbk = () => { },
    showLimit = false
  } = props || {};

  const { planName = "", inFreePlan = false } =
    userDetail?.userFeatureFlag || {};

  const { selectedConnectedInbox = [], setSelectedConnectedInbox = () => { } } =
    sequenceStoreData;

  const disabledClassName =
    selectedConnectedInbox?.length > 1 ? "" : "disabled";

  const inboxRequired =
    SequenceUtils.checkEmailSteps(sequence) && !selectedConnectedInbox?.length;

  const [showDropdown, setShowDropdown] = useState(false);
  const [inboxes, setInboxes] = useState([]);
  const [inboxUsage, setInboxUsage] = useState({});
  const [dropdownLoader, setDropdownLoader] = useState(true);
  const [inboxLoaderId, setInboxLoaderId] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [tempSelection, setTempSelection] = useState([]);
  const [isAddingInboxes, setIsAddingInboxes] = useState(false);
  const [selectedInboxForPopup, setSelectedInboxForPopup] = useState(null);

  const activeInboxes =
    inboxes?.filter(item => item?.status === "ACTIVE") || [];

  // No inbox on the whole account: the Add button becomes a one-click
  // "Connect inbox" redirect instead of opening an empty picker.
  const hasAccountInboxes = (userDetail?.connectedInboxes || []).some(
    item => item?.status === "ACTIVE"
  );

  const redirectToConnectInbox = () => {
    Utils.mixpanelTrack(MP_EVENT.SQ_MULTI_INBOX_LEARN_MORE_CLICKED, {
      pageType: "Sequence Settings"
    });
    cbk();
    history.push("/settings/account");
  };

  const HandleConnectInboxAction = () => {
    if (InboxUtils.connectInboxAllowed()) {
      redirectToConnectInbox();
    } else {
      PaywallUtils.upgradeUtilsFunction(
        "multipleInbox",
        false,
        planName,
        inFreePlan
      );
    }
  };

  const removeInboxFromSequence = async (id = "", cbk = () => { }) => {
    const confirmationCbk = async () => {
      await sequenceStoreData.removeInboxFromSequence(
        {
          inboxId: id,
          sequenceId
        },
        cbk
      );
    };

    confirmationPopupState.setPopupValues({
      content: `Are you sure you want to remove this inbox from the sequence?
        All the scheduled emails from this inbox will be suspended.`,
      actionBtnText: "Proceed",
      callback: () => confirmationCbk(),
      loadingMsg: "Please wait..."
    });
    confirmationPopupState.setShowPopup(true);
  };

  const fetchInboxUsage = async () => {
    try {
      const response = await makeApi({
        url: URLS.getInboxUsage,
        method: "GET"
      });
      if (response?.data) {
        const usageMap = {};
        response.data.forEach(item => {
          usageMap[item.inboxId] = item;
        });
        setInboxUsage(usageMap);
      }
    } catch (error) {
      console.error("Error fetching inbox usage:", error);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleActionIconClick = useCallback((id, email) => {
    setSelectedInboxForPopup({ id, email });
  }, []);

  const closeInboxPopup = () => {
    setSelectedInboxForPopup(null);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const activeInboxes = inboxes.filter(item => item?.status === "ACTIVE");
      const filtered = activeInboxes.filter(item =>
        item?.email.toLowerCase().includes(searchText.toLowerCase())
      );
      const newSelection = filtered.map(item => ({ id: item.id, email: item.email }));
      const uniqueSelection = [...tempSelection];
      newSelection.forEach(item => {
        if (!uniqueSelection.some(ele => ele.id === item.id)) {
          uniqueSelection.push(item);
        }
      });
      setTempSelection(uniqueSelection);
    } else {
      const filtered = inboxes.filter(item =>
        item?.status === "ACTIVE" && item?.email.toLowerCase().includes(searchText.toLowerCase())
      );
      const remainingSelection = tempSelection.filter(item =>
        !filtered.some(ele => ele.id === item.id)
      );
      // Keep already selected inboxes that are not in the current filtered list
      setTempSelection(remainingSelection);
    }
  };

  const handleChange = (checked, id, label) => {
    if (checked) {
      setTempSelection([...tempSelection, { id, email: label }]);
    } else {
      setTempSelection(tempSelection.filter(item => item.id !== id));
    }
  };

  const handleBatchAddInboxes = async () => {
    if (tempSelection.length === 0) return;

    setIsAddingInboxes(true);
    const inboxIds = tempSelection.map(item => item.id);

    const cbk = (success) => {
      if (success) {
        const newSelected = [...selectedConnectedInbox];
        tempSelection.forEach(item => {
          if (!newSelected.some(ele => ele.id === item.id)) {
            newSelected.push(item);
          }
        });
        setSelectedConnectedInbox(newSelected);
        getSelectedSeqData();
        setShowDropdown(false);
        setTempSelection([]);
      }
      setIsAddingInboxes(false);
    };

    try {
      const response = await makeApi({
        url: `${URLS.addInboxesToSeq}${sequenceId}`,
        method: "POST",
        data: inboxIds
      });
      cbk(response?.status === 200);
    } catch (error) {
      console.error("Error adding inboxes:", error);
      cbk(false);
    }
  };

  const removeEmail = async (email = "", id = "") => {
    const cbk = () => {
      let temp = [...selectedConnectedInbox];
      temp = temp.filter(item => item?.email !== email);
      setSelectedConnectedInbox([...temp]);
      confirmationPopupState.setShowPopup(false);
    };
    if (editSequencePopupState?.totalContacts > 0) {
      removeInboxFromSequence(id, cbk);
    } else {
      await sequenceStoreData.removeInboxFromSequence(
        {
          inboxId: id,
          sequenceId
        },
        cbk
      );
    }
  };

  const handleReassignContactChange = (value = "") => {
    setReAssignContact(value);
    sequenceStoreData.setTempThrottlingData({
      ...sequenceStoreData.tempThrottlingData,
      reassignInbox: value
    });
  };

  const constructDropDownData = async () => {
    setInboxes(userDetail?.connectedInboxes);
    setDropdownLoader(true);
    await fetchInboxUsage();
    setDropdownLoader(false);
  };

  const isReassignInboxDisabled = () => {
    return isSharedSequence && !SequenceUtils.isSequenceOwner(sequenceMemberId);
  };

  const inputObj = {
    label: "Automatically reassign contacts if an inbox gets disconnected.",
    id: "autoReassignContact",
    cbk: handleReassignContactChange,
    checked: reAssignContact
  };

  useEffect(() => {
    constructDropDownData();
    return () => {
      setInboxes([]);
    };
  }, [isSharedSequence, userDetail?.connectedInboxes]);

  return (
    <div className={`sequenceMultiInboxSelection ${className}`}>
      {showHeader && (
        <div className="inboxSectionHeader">
          <div className="inboxSectionTitle">
            <h6>{title}</h6>
            {inboxRequired ? (
              <span className="requiredChip">Required</span>
            ) : (
              selectedConnectedInbox?.length > 0 && (
                <span className="inboxCountBadge">
                  {selectedConnectedInbox.length} connected
                </span>
              )
            )}
          </div>
        </div>
      )}
      {hasSubLabel && (
        <p className="subTextForSettings">
          Emails are sent from these inboxes. Contacts are split evenly across
          them.{" "}
          <button className="hereText" onClick={redirectToConnectInbox}>
            Learn more.
          </button>
        </p>
      )}
      {isInboxMappedToSequence && !selectedConnectedInbox?.length && (
        <p className="inboxEmptyStateLine">
          {inboxRequired
            ? sequence?.status?.toLowerCase() === "active"
              ? "This sequence has email steps but no inbox connected. Emails cannot send."
              : "This sequence has email steps. Connect at least one inbox to activate it."
            : "No inboxes connected yet."}
        </p>
      )}
      <div className="addInboxAnchor">
        <button
          type="button"
          className="addInboxBtn"
          onClick={() =>
            hasAccountInboxes ? setShowDropdown(true) : HandleConnectInboxAction()
          }
        >
          <i className="material-icons">add</i>
          <span className="btnText">
            {hasAccountInboxes ? "Add inbox" : "Connect inbox"}
          </span>
        </button>
        {showDropdown && (
          <>
            <div className="dropdownInboxList">
              <div className="dropdownOptions">
                {!dropdownLoader && !activeInboxes.length ? (
                  <div className="noInboxConnectedState">
                    <div className="noInboxText">
                      You haven't connected your inbox yet. Connect one to
                      start sending emails.
                    </div>
                    <button
                      type="button"
                      className="connectInboxCtaBtn"
                      onClick={HandleConnectInboxAction}
                    >
                      Connect inbox
                    </button>
                  </div>
                ) : (
                  <>
              <div className="searchWrapper">
                <SearchInputBox
                  placeholder="Search by email or name"
                  valueCbk={handleSearch}
                  hasCloseIcon={true}
                  hasSearchIcon={false}
                />
              </div>
              <div className="selectAllWrapper">
                {(() => {
                  const filtered = inboxes?.filter(
                    item =>
                      item?.status === "ACTIVE" &&
                      item?.email.toLowerCase().includes(searchText.toLowerCase())
                  ) || [];
                  const isAllSelected = filtered.length > 0 && filtered.every(item =>
                    selectedConnectedInbox.some(ele => ele.id === item.id) ||
                    tempSelection.some(ele => ele.id === item.id)
                  );
                  const isSelectAllDisabled = filtered.length === 0 || filtered.every(item =>
                    selectedConnectedInbox.some(ele => ele.id === item.id)
                  );
                  return (
                    <SecondaryCheckbox
                      label="Select All"
                      id="selectAllInboxes"
                      cbk={handleSelectAll}
                      checked={isAllSelected}
                      disabled={isSelectAllDisabled}
                    />
                  );
                })()}
              </div>
              <div className="inboxList">
                {dropdownLoader ? (
                  <DropDownLoader />
                ) : (
                  (() => {
                    const filtered = inboxes?.filter(
                      item =>
                        item?.status === "ACTIVE" &&
                        item?.email.toLowerCase().includes(searchText.toLowerCase())
                    ) || [];
                    return filtered.length > 0 ? (
                      filtered.map(item => {
                        const { id = "", email = "" } = item || {};
                        const usage = inboxUsage[id] || {};
                        return (
                          <InboxListItem
                            key={id}
                            id={id}
                            label={email}
                            totalSequences={usage.totalSequences || 0}
                            activeSequences={usage.activeSequences || 0}
                            cbk={handleChange}
                            checked={tempSelection.some(ele => ele.id === id)}
                            disabled={selectedConnectedInbox?.some(ele => ele.id === id)}
                            onActionIconClick={handleActionIconClick}
                          />
                        );
                      })
                    ) : (
                      <div className="inboxEmptyState">
                        <span>No inboxes found</span>
                      </div>
                    );
                  })()
                )}
              </div>
              <div className="dropdownFooter">
                <button
                  className={`addMultipleInboxesBtn ${tempSelection.length === 0 ? "disabled" : ""}`}
                  onClick={handleBatchAddInboxes}
                  disabled={tempSelection.length === 0 || isAddingInboxes}
                >
                  {isAddingInboxes ? "Adding..." : (tempSelection.length === 0 ? "Select inboxes to add" : `Add ${tempSelection.length} Inbox${tempSelection.length > 1 ? "es" : ""}`)}
                </button>
                <div
                  className="connectInboxFullRow"
                  onClick={HandleConnectInboxAction}
                >
                  <i className="material-icons">add</i>
                  <span className="btnText">Connect new inbox</span>
                </div>
              </div>
                  </>
                )}
              </div>
            </div>
            <div
              className="multiInboxDropdownOverlay"
              onClick={() => {
                setShowDropdown(false);
                setTempSelection([]);
                setSearchText("");
                setSelectedInboxForPopup(null);
              }}
            />
          </>
        )}
      </div>
      {selectedInboxForPopup && (
        <UsageAuditModal
          inboxId={selectedInboxForPopup.id}
          email={selectedInboxForPopup.email}
          onClose={closeInboxPopup}
        />
      )}
      {selectedConnectedInbox?.length > 0 && (
        <SelectedInboxList
          inboxes={selectedConnectedInbox}
          loading={!isInboxMappedToSequence}
          canRemove={!disabledClassName}
          onRemove={removeEmail}
          showLimit={showLimit}
          inboxUsage={inboxUsage}
        />
      )}
      {showReassignInbox && (
        <div
          className={`TriggerProspect ${isReassignInboxDisabled() ? "disabled" : ""
            }`}
        >
          <SecondaryCheckbox
            key={inputObj?.id}
            {...inputObj}
            disabled={isReassignInboxDisabled()}
          />
        </div>
      )}
    </div>
  );
});

const DropDownLoader = () => {
  return (
    <div className="inboxLoader">
      {Array.from({ length: 3 }, (value, index) => (
        <div key={`${index}-${value}`} className="linear-background" />
      ))}
    </div>
  );
};

export default MultiInboxSelection;
export { MultiInboxSelection };
